import { Input,  Component, OnInit, forwardRef, OnDestroy, ChangeDetectorRef, OnChanges } from '@angular/core';

import { BehaviorSubject, Subscription, filter, map, tap } from 'rxjs';

import { AbstractFormFieldComponent } from '../../FormField/abstract-form-field.component';
import { CoreDialogService } from '../../../../../Core/Dialogs/services/dialog.service';
import {
    ManageContentLibraryDialogComponent
} from '../../../../ContentLibrary/components/ManageContentLibraryDialog/manage-content-library-dialog.component';
import { FullProfileAnimationModel } from '../../../../../Core/Dialogs/models/full-profile-animation.model';
import { FullProfileDialogAnimationsEnum } from '../../../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '../../../../../Core/Dialogs/enums/overlay-animation.enum';
import { MultiContentLibraryFieldModel } from '../../../models/multi-content-library-field-model';
import { MediaSummaryModel } from '../../../../ContentLibrary/models/content-library-media-summary.model';
import { MessageService } from 'primeng/api';
import { LocalizationService } from '../../../../../Core/Localization/services/localization.service';
import { ButtonTypesEnum } from '../../../../Forms/enums/button-types.enum';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { messageSeverityEnum } from '../../../../Misc/enums/message-severity.enum';
import { ContentLibraryMediaService } from '../../../../ContentLibrary/services/content-library-media.service';
import { MediaServiceApi } from '../../../../../api/mediaService/media-service.api';
import { MediaServiceAccessTypeEnum } from '../../../../../api/mediaService/enums/media-service-access-type.enum';
import { MediaServiceErrorCodesEnum } from '../../../../../api/mediaService/mediaResults.api.model';
import { BriefingImagePropertiesModel } from '@Common/PageBuilder/models/briefing-image-properties.model';
import { BlockDisplayTypeEnum } from '@Common/PageBuilder/enums/block-display-type.enum';
import { BriefingImageDisplayElementModel } from '@Common/PageBuilder/models/briefing-image-display-element.model';
import { BriefingImageFrameEnum } from '@Monitor/Shared/enums/briefing-image-frame.enum';
import { ImageSourceEnum } from '../../../enums/image-source.enum';
import { CoverageImageDialogComponent } from '@Monitor/Briefings/components/CoverageImageDialog/coverage-image-dialog.component';
import { CoverageImage } from '@Monitor/Shared/models/coverage-image.model';

@Component({
    selector: 'agpr-multi-content-library-panel',
    templateUrl: './multi-content-library-panel.component.html',
    styleUrls: ['./multi-content-library-panel.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => MultiContentLibraryPanelFieldComponent),
            multi: true
        }
    ],
    standalone: false
})
export class MultiContentLibraryPanelFieldComponent extends AbstractFormFieldComponent implements ControlValueAccessor, OnInit, OnDestroy, OnChanges {
    @Input() override field: MultiContentLibraryFieldModel;

    readonly buttonTypes = ButtonTypesEnum;
    readonly messageSeverityEnum = messageSeverityEnum;
    readonly imageSources = ImageSourceEnum;

    contentLibraryImages: MediaSummaryModel[] = [];
    value: BriefingImagePropertiesModel[] = [];
    imageUrls: string[] = [];
    acceptedFileTypes: string[] = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    altText: string;
    isCanvas: boolean = false;
    showFirstImageWarning: boolean = false;
    maxFiles: number = 1;
    uploadIcon: string;
    hasAutomateImage: boolean = false;

    uploadOptions = [
        {
            label: 'Mention.editDialog.image.label',
            command: () => {
                const blankImage = new BriefingImagePropertiesModel();
                blankImage.touched = false;
                if (this.isCanvas) {
                    this.value.push(blankImage);
                } else {
                    this.value = [blankImage];
                }
                this.emitChanges();
            }
        },
        {
            label: 'Mention.editDialog.image.browser',
            description: 'Mention.editDialog.image.browser.description',
            command: () => {
                this.onImageUpload();
            }
        },
        {
            label: 'Mention.editDialog.image.contentLibrary',
            command: () => {
                this.openContentLibrary();
            }
        }
    ];

    subscriptions: Subscription[] = [];

    constructor(
        public coreDialogService: CoreDialogService,
        private messageService: MessageService,
        private localizationService: LocalizationService,
        public readonly contentLibraryMediaService: ContentLibraryMediaService,
        private mediaServiceApi: MediaServiceApi,
        private cd: ChangeDetectorRef
    ) {
        super();
    }

    onModelChange: Function = () => {};
    onModelTouched: Function = () => {};

    ngOnInit(): void {
        this.subscriptions.push(
            this.contentLibraryMediaService.all$.subscribe(media => {
                this.contentLibraryImages = media;
                if (this.value?.length) {

                    this.imageUrls = this.value.map(v => v.url);
                    const files = media.filter(item => {
                        return this.imageUrls.includes(item.url);
                    }).map(file => ({
                        id: file.id,
                        name: file.name,
                        size: file.size,
                        url: file.url,
                        mimeType: file.mimeType,
                    }));

                    this.value = this.mergeByUrl(this.value, files).map(val => new BriefingImagePropertiesModel(val));
                    this.value?.forEach(file => this.urlBlur(file));
                }
            })
        );

        if (!this.field?.hideAutomateImageOption) {
            this.uploadOptions.push(
                {
                    label: 'Mention.editDialog.image.automate',
                    command: () => {
                        const blankImage = new BriefingImagePropertiesModel();
                        blankImage.imageSource = ImageSourceEnum.AUTOMATE;

                        blankImage.touched = false;
                        if (this.isCanvas) {
                            this.value.push(blankImage);
                        } else {
                            this.value = [blankImage];
                        }
                        this.checkHasAutomateImage();
                        this.emitChanges();
                    }
                },
                {
                    label: 'Mention.editDialog.image.select',
                    command: () => {
                        this.openCoverageImageDialog();
                    }
                }
            );
        }
    }

    ngOnChanges() {
        this.isCanvas = this.block?.displayElement?.displayType === BlockDisplayTypeEnum.ONLINE;
        if (!this.value) {
            this.value = [];
        }

        if (this.field?.maxFiles){
            this.maxFiles = this.field.maxFiles;
        }
        if (!this.isCanvas) {
            this.maxFiles = 1;
        }
        this.value?.forEach(file => this.urlBlur(file));
        this.checkHasAutomateImage();
        this.checkFirstImageWarning();
        this.setLocation();
        this.updateUploadIcon();
    }

    onImageUpload(): void {
        const fileInput = document.createElement('input');
        fileInput.type = 'file';
        fileInput.accept = 'image/*';
        fileInput.onchange = (event) => {
            const file = (event.target as HTMLInputElement).files?.[0];
            if (file) {
                this.uploadFile(file);
            }
            fileInput.value = '';
        };
        fileInput.click();
    }

    private uploadFile(file: File) {
        this.mediaServiceApi.upload([file], MediaServiceAccessTypeEnum.PUBLIC).pipe(
            map(response => {
                if (response.mediaResults[0].codedError?.code === MediaServiceErrorCodesEnum.duplicateFile) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'Common.contentLibrary.Browserupload.error',
                        detail: 'Common.contentLibrary.Browserupload.error.message'
                    });
                    return null;
                } else if (response.mediaResults[0].codedError?.code === MediaServiceErrorCodesEnum.fileMaxSize) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'Common.mediaService.fileUpload.maxSize.heading',
                        detail: this.localizationService.transform('Common.mediaService.fileUpload.maxSize.message', { name: file.name })
                    });
                    return null;
                }  else if (response.mediaResults[0].codedError) {
                    // fail safe if any image fails
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'Common.contentLibrary.Browserupload.error',
                        detail: 'Common.lhp.libraryMoveDialog.upload.error.message'
                    });
                    return null;
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'Common.contentLibrary.Browserupload',
                        detail: 'Common.contentLibrary.Browserupload.message'
                    });
                }
                return {
                    url: response.mediaResults[0].media.url,
                    name: response.mediaResults[0].media.name,
                    id: response.mediaResults[0].media.id,
                    size: response.mediaResults[0].media.size
                };
            }),
            filter(fileUpload => fileUpload !== null),
            tap(fileUpload => {
                const newFile = new BriefingImagePropertiesModel(fileUpload);
                if (this.isCanvas) {
                    this.value.push(newFile);
                } else {
                    this.value = [newFile];
                }
                this.emitChanges();
                this.cd.detectChanges();
                this.contentLibraryMediaService.refreshAll();
            })
        ).subscribe({
            error: (error) => {
                if (error.status === 429) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: this.localizationService.transform('Common.mediaService.fileUpload.notEnoughSpace.heading'),
                        detail: this.localizationService.transform('Common.mediaService.fileUpload.notEnoughSpace.message', { name: file.name })
                    });
                }
            }
        });
    }

    openContentLibrary(): void {
        const overlayContainer = document.getElementById('main-content');
        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );

        const io = {
            inputs: {
                animationTrigger: animationTrigger,
                overlayContainer: overlayContainer,
                selectionMode: true,
                singleSelect: !this.isCanvas,
                acceptedFileTypes: this.acceptedFileTypes,
                navBarLaunch: false
            },
            outputs: {
                dialogClose: (selectedFiles: MediaSummaryModel[]) => {
                    if (!selectedFiles?.length) {
                        return;
                    }

                    const invalidFiles = selectedFiles.filter(file =>
                        !this.acceptedFileTypes.includes(file.mimeType)
                    );

                    if (invalidFiles.length > 0) {
                        this.messageService.add({
                            severity: messageSeverityEnum.error,
                            summary: this.localizationService.transform('PageBuilder.error.fileTypeHeader'),
                            detail: this.localizationService.transform('PageBuilder.error.fileTypeMessage', {
                                types: io.inputs.acceptedFileTypes.map(type => type.replace('image/', '')).join(', ')
                            })
                        });
                        return;
                    }
                    let fileUploads: BriefingImagePropertiesModel[] = selectedFiles.map(file => new BriefingImagePropertiesModel(file));
                    if (this.isCanvas) {
                        if ((this.value?.length || 0) + selectedFiles.length > this.maxFiles) {
                            this.messageService.add({
                                severity: messageSeverityEnum.error,
                                summary: this.localizationService.transform('Error'),
                                detail: this.localizationService.transform('Common.contentLibrary.maxFiles', {
                                    remaining: this.maxFiles - (this.value?.length || 0)
                                })
                            });
                            return;
                        }
                        fileUploads = [...this.value, ...fileUploads];
                    } else {
                        fileUploads = [fileUploads[0]];
                    }

                    this.onFileUploadChange(fileUploads);
                }
            }
        };

        this.coreDialogService.open(ManageContentLibraryDialogComponent, io);
    }

    openCoverageImageDialog(): void {
        const overlayContainer = document.getElementById('main-content');
        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );

        let maxFiles = 1;
        if (this.isCanvas) {
            maxFiles = 4 - this.value?.length;
        }

        const io = {
            inputs: {
                animationTrigger: animationTrigger,
                overlayContainer: overlayContainer,
                maxFiles,
            },
            outputs: {
                dialogClose: (selectedFiles: CoverageImage[]) => {
                    if (!selectedFiles?.length) {
                        return;
                    }

                    let fileUploads: BriefingImagePropertiesModel[] = selectedFiles.map(file => {
                        const newFile = new BriefingImagePropertiesModel();
                        newFile.noThumbnail = true;
                        newFile.imageSource = ImageSourceEnum.NORMAL;
                        newFile.url = file.url;

                        return newFile;
                    });
                    if (this.isCanvas) {
                        fileUploads = [...this.value, ...fileUploads];
                    } else {
                        fileUploads = [fileUploads[0]];
                    }

                    this.onFileUploadChange(fileUploads);
                }
            }
        };

        this.coreDialogService.open(CoverageImageDialogComponent, io);
    }



    // function that merges 2 different arrays, and if the object url matches, it will combine both objects
    // used to combine MediaSummaryModel into BriefingImagePropertiesModel
    mergeByUrl(array1, array2) {
        return array1.map(itm => ({
            ...array2.find((item) => (item.url === itm.url) && item),
            ...itm
        }));
    }

    setLocation() {
        this.value?.forEach((file, index) => file.location = index);
    }

    changeAltText(file: BriefingImagePropertiesModel) {
        if (!file.showAltText) {
            file.altText = null;
        }
        this.emitChanges();
    }

    emitChanges(): void {
        this.value?.forEach(file => this.urlBlur(file));
        this.checkFirstImageWarning();
        this.setLocation();
        this.changes.emit(this.value);
    }

    checkFirstImageWarning() {
        if (this.value?.length > 1 && (!(this.block?.displayElement as BriefingImageDisplayElementModel)?.imageEffects?.transition) &&
            ((this.block?.displayElement as BriefingImageDisplayElementModel)?.imageEffects?.imageFrame === BriefingImageFrameEnum.NO_FRAME)) {
            this.showFirstImageWarning = true;
        } else {
            this.showFirstImageWarning = false;
        }
    }

    removeFile(index: number): void {
        this.value.splice(index, 1);
        this.onFileUploadChange(this.value);
        this.checkHasAutomateImage();
    }

    urlBlur(file: BriefingImagePropertiesModel) {
        // skips validation if the user hasn’t interacted with the input yet.
        if (!file.touched) {
            return true;
        }
        file.hasError = false;
        file.errorMessage = null;
        if (!file?.url && file.imageSource !== ImageSourceEnum.AUTOMATE) {
            file.errorMessage = 'Briefings.error.invalidImageUrl';
            file.hasError = true;
            return false;
        }
        file.url = file.url?.trim();

        if (file.imageSource !== ImageSourceEnum.AUTOMATE) {
            file.url = this.normalizeImageUrl(file.url);

            let parsed: URL;
            try {
                parsed = new URL(file.url);
            } catch {
                file.errorMessage = 'Briefings.error.invalidImageUrl';
                file.hasError = true;
                return false;
            }

            if (!['https:', 'http:'].includes(parsed.protocol)) {
                file.errorMessage = 'Briefings.error.invalidImageUrl';
                file.hasError = true;
                return false;
            }
        }
    }

    private normalizeImageUrl(url: string): string {
        if (!url) {
            return url;
        }

        if (url.startsWith('//')) {
            url = 'https:' + url;
        }

        if (url.startsWith('http://')) {
            url = 'https://' + url.slice(7);
        }

        url = url.replace(/ /g, '%20');
        return url;
    }

    /**
     * Handles user input for the URL field.
     * Updates the file's URL value and marks the file as "touched".
     */
    onUrlInput(file: BriefingImagePropertiesModel, event: Event): void {
        const input = event.target as HTMLInputElement;
        file.url = input.value;
        if (!file.touched) {
            file.touched = true;
        }
    }

    /**
     * Handles when the URL input loses focus (blur event).
     * Marks the file as "touched", validates the URL, and emits changes.
     */
    onUrlBlur(file: BriefingImagePropertiesModel): void {
        file.touched = true;
        this.urlBlur(file);
        this.emitChanges();
    }

    updateUploadIcon(): void {
        if (!this.isCanvas && this.value?.length === 1) {
            this.uploadIcon = 'fa-regular fa-duotone fa-pen';
        } else {
            this.uploadIcon = 'fa-regular fa-duotone fa-square-plus';
        }
    }

    moveUp(index: number) {
        if (index === 0) {
            // already at the top, so nothing to move
            return;
        }
        const movedImage = this.value.splice(index, 1);
        this.value.splice(index - 1, 0, ...movedImage);
        this.emitChanges();
    }

    moveDown(index: number) {
        if (index === this.value.length - 1) {
            // already at the bottom, so nothing to move
            return;
        }
        const movedImage = this.value.splice(index, 1);
        this.value.splice(index + 1, 0, ...movedImage);
        this.emitChanges();
    }

    onFileUploadChange(newFileList: BriefingImagePropertiesModel[]): void {
        if (!this.isCanvas && (newFileList?.length > 1)) {
            newFileList = [newFileList[0]];
        }
        this.value = newFileList || [];
        this.emitChanges();
        this.updateUploadIcon();
    }

    checkHasAutomateImage(): void {
        this.hasAutomateImage = false;
        this.value?.forEach(file => {
            if (file.imageSource === ImageSourceEnum.AUTOMATE) {
                this.hasAutomateImage = true;
            }
        });
    }

    writeValue(value:  BriefingImagePropertiesModel[]): void {
        this.value = value;
    }

    registerOnChange(fn: Function): void {
        this.onModelChange = fn;
    }

    registerOnTouched(fn: Function): void {
        this.onModelTouched = fn;
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
